import { memo } from 'react';
import { Box, Card, DropdownSelect, Tag, Text } from '@zillow/constellation-10';
import type { YourRentalCtaDto, YourRentalStatusTag } from '@zg-rentals/ts-your-rental-api-client';
import CtaButton from '../shared/CtaButton';
import { TAG_APPEARANCE_MAP } from '../../constants';

type TagsProps = {
  tags: Array<YourRentalStatusTag>;
};

const Tags = memo(({ tags }: TagsProps) => {
  return (
    <Box
      css={{
        display: 'flex',
        gap: 'tighter',
        flexWrap: 'wrap',
      }}
    >
      {tags.map((tag, index) => (
        <Tag key={index} tone={TAG_APPEARANCE_MAP[tag.tagAppearance]}>
          {tag.tagText}
        </Tag>
      ))}
    </Box>
  );
});

Tags.displayName = 'Tags';

type CtaButtonsProps = {
  ctas: Array<YourRentalCtaDto>;
  listingAlias?: string;
};

const CtaButtons = memo(({ ctas, listingAlias }: CtaButtonsProps) => {
  return (
    <Box
      css={{
        display: 'flex',
        gap: 'tighter',
        width: '100%',
      }}
    >
      {ctas.map((cta, index) => (
        <CtaButton
          key={index}
          cta={cta}
          listingAlias={listingAlias}
          size="sm"
          fluid={true}
          dataTestId={`ActiveCard-Cta-${cta.action}`}
        />
      ))}
    </Box>
  );
});

CtaButtons.displayName = 'CtaButtons';

type ActiveCardProps = {
  title: string;
  dataTestId: string;
  icon: React.ReactNode;
  dropdownOptions?: Array<string> | Array<{ value: string; label: string }>;
  dropdownValue?: string;
  dropdownShowLabelForValue?: boolean;
  onDropdownChange?: (value: string) => void;
  dropdownAriaLabel?: string;
  tags: Array<YourRentalStatusTag>;
  ctas: Array<YourRentalCtaDto>;
  listingAlias?: string;
  children: React.ReactNode;
};

const ActiveCard = memo(
  ({
    title,
    dataTestId,
    icon,
    dropdownOptions,
    dropdownValue,
    dropdownShowLabelForValue,
    onDropdownChange,
    dropdownAriaLabel,
    tags,
    ctas,
    listingAlias,
    children,
  }: ActiveCardProps) => {
    return (
      <Card
        elevated={false}
        data-testid={dataTestId}
        css={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          padding: '{spacing.default} {spacing.loose} {spacing.loose}',
          flex: 1,
          height: '100%',
        }}
      >
        <Box
          css={{
            display: 'flex',
            alignItems: 'center',
            gap: 'tighter',
            paddingBottom: 'default',
            borderBottom: '{borderWidths.default} solid {colors.gray.300}',
            width: '100%',
          }}
        >
          {icon}
          <Text textStyle="heading-xs">{title}</Text>
        </Box>
        <Box
          css={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 'tighter',
            padding: '{spacing.default} 0',
            flexGrow: 1,
            width: '100%',
          }}
        >
          {dropdownOptions && dropdownValue && onDropdownChange && (
            <DropdownSelect
              options={dropdownOptions}
              value={dropdownValue}
              onChange={onDropdownChange}
              showLabelForValue={dropdownShowLabelForValue}
              css={{ width: '100%', marginBottom: 'tighter' }}
              aria-label={dropdownAriaLabel || 'Select option'}
            />
          )}
          <Tags tags={tags} />
          {children}
        </Box>
        <CtaButtons ctas={ctas} listingAlias={listingAlias} />
      </Card>
    );
  },
);

ActiveCard.displayName = 'ActiveCard';

export default ActiveCard;
